/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "antd";
import dayjs from "dayjs";

import { countryListActions, selectCountryList, selectTimezone, timezoneActions } from "Features/utils";
import { selectUser } from "Features/user";
import { selectError } from "Features/error";
import { authActions, selectAuth } from "Features/auth";
import { CalendarIcon } from "Shared/icons";
import { Button, Checkbox, Input, DropdownCustom, UploadAvatar } from "Shared/ui";

const state = {
	login: "",
	first_name: "",
	last_name: "",
	birthday: "",
	gender: "",
	country_id: "",
	timezone_id: "",
	photo: "",
};

export const PostRegPage = () => {
	const dispatch = useDispatch();
	const user = useSelector(selectUser?.data);
	const authSession = useSelector(selectAuth.authSession);
	const authFetch = useSelector(selectAuth.fetch);
	const countries = useSelector(selectCountryList.list) || [];
	const [countryItems, setCountryItems] = useState([]);
	const timezones = useSelector(selectTimezone.list);
	const reduxErrors = useSelector(selectError.message);
	const [errors, setErrors] = useState({ first_name: null, last_name: null, birthday: null });
	const [timeZoneItems, setTimeZoneItems] = useState([]);
	const [localImageUrl, setLocalImageUrl] = useState("");
	const [userData, setUserData] = useState({
		login: "",
		first_name: "",
		last_name: "",
		birthday: "",
		gender: "",
		country_id: "",
		timezone_id: "",
		photo: "",
	});

	function updateUserData(key, value) {
		state[key] = value;
		setUserData(state);
		setErrors({ ...errors, [key]: null });
	}
	const updateLogin = (value) => updateUserData("login", value);
	const updateFirstname = (value) => updateUserData("first_name", value);
	const updateLastname = (value) => updateUserData("last_name", value);
	const updateBirthday = (value) => updateUserData("birthday", value ? new Date(value).toISOString() : "");
	const updateGender = (value) => updateUserData("gender", value);
	const updateCountry = (value) => updateUserData("country_id", typeof value === "number" ? value : Number(value.key));
	const updateTz = (value) => updateUserData("timezone_id", typeof value === "number" ? value : Number(value.key));
	const updatePhoto = (value) => updateUserData("photo", value);

	useEffect(() => {
		const newErrors = { ...errors };
		if (reduxErrors?.length) {
			reduxErrors?.forEach((error) => {
				if (error?.includes("first_name")) {
					if (newErrors.first_name) newErrors.first_name.push(error);
					if (!newErrors.first_name) newErrors.first_name = [error];
				}
				if (error?.includes("last_name")) {
					if (newErrors.last_name) newErrors.last_name.push(error);
					if (!newErrors.last_name) newErrors.last_name = [error];
				}
			});
		}
		setErrors({ ...newErrors });
	}, [reduxErrors]);

	useEffect(() => {
		if (userData.login !== user?.login && user?.login) updateLogin(user?.login);
		if (userData.first_name !== user?.first_name && user?.first_name) updateFirstname(user?.first_name);
		if (userData.last_name !== user?.last_name && user?.last_name) updateLastname(user?.last_name);
		if (userData.birthday !== user?.birthday && user?.birthday) updateBirthday(user?.birthday);
		if (userData.gender !== user?.gender && user?.gender) updateGender(user?.gender);
		if (userData.photo !== user?.photo && user?.photo) updatePhoto(user?.photo);
		if (userData.country_id !== user?.country?.id && user?.country?.id) updateCountry(user?.country?.id);
		if (userData.timezone_id !== user?.timezone?.id && user?.timezone?.id) updateTz(user?.timezone?.id);
		// setUserData({
		// 	login: user?.login || "",
		// 	first_name: user?.first_name || "",
		// 	last_name: user?.last_name || "",
		// 	birthday: user?.birthday || "",
		// 	gender: user?.gender || "",
		// 	country_id: user?.country?.id || "",
		// 	timezone_id: user?.timezone?.id || "",
		// 	photo: user?.photo || "",
		// });
	}, [user]);

	useEffect(() => {
		setCountryItems(
			countries.map((item) => {
				return {
					key: item.id,
					label: item.name,
				};
			}),
		);
		if (countries.length === 0) {
			dispatch(countryListActions.get());
		} else {
			fetch("https://ipapi.co/json/")
				.then((response) => response.json())
				.then((data) => {
					const current = countries.find((country) => country.iso_code === data.country_code);
					updateCountry(current.id);
				});
		}
	}, [countries]);

	useEffect(() => {
		setTimeZoneItems(
			timezones.map((item) => {
				return {
					key: item.id,
					label: item.name,
				};
			}),
		);
		if (timezones.length === 0) {
			dispatch(timezoneActions.get());
		} else {
			const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const tz = timezones.find((tzone) => tzone.name === browserTz);
			updateTz(tz.id);
		}
	}, [timezones]);

	function onSave() {
		const formData = new FormData();
		const dataKeys = Object.keys(userData);
		const checkErrors = { ...errors };
		if (!userData.first_name) checkErrors.first_name = "required field";
		if (!userData.last_name) checkErrors.last_name = "required field";
		if (!userData.birthday) checkErrors.birthday = "required field";
		if (dataKeys.some((key) => checkErrors[key])) {
			setErrors(checkErrors);
			return;
		}
		dataKeys.forEach((key) => {
			if (userData[key] !== user[key]) formData.append(key, userData[key]);
		});
		dispatch(authActions.update(formData));
	}

	function countryLabel() {
		if (userData.country_id !== user?.country?.id) {
			return countryItems.find((item) => item.key === userData.country_id)?.label;
		}
		return user?.country?.name;
	}

	function timezoneLabel() {
		if (userData.timezone_id !== user?.timezone?.id) {
			return timeZoneItems.find((item) => item.key === userData.timezone_id)?.label;
		}
		return user?.timezone?.name;
	}

	return (
		<div className="auth">
			<div className="auth__container auth__container_w500">
				<div className="auth__block mb-30">
					<div className="auth__block-text  mb-10">
						<span className="auth__block-text_big">Personal Data</span>
					</div>
				</div>
				<div style={{ display: "flex", justifyContent: "flex-start", width: "100%", alignItems: "center" }}>
					<div className="mr-16">
						<UploadAvatar
							photoUrl={user?.photo}
							localImageUrl={localImageUrl}
							setLocalImageUrl={setLocalImageUrl}
							setFile={updatePhoto}
						/>
					</div>

					<div style={{ display: "flex", flexDirection: "column" }}>
						<span className="auth__block-text_middle">Photo</span>
						<span className="auth__block-text_small">Upload your photo.</span>
					</div>
				</div>

				<Input
					placeholder="Login"
					type={"text"}
					className="mb-17"
					outValue={userData.login}
					setOutValue={updateLogin}
					errorMessage={errors.login}
					disabled={!authSession.social_profile_id}
				/>
				<Input
					placeholder="Firstname*"
					type={"text"}
					className="mb-17"
					outValue={userData.first_name}
					setOutValue={updateFirstname}
					errorMessage={errors.first_name}
				/>
				<Input
					placeholder="Surname*"
					type={"text"}
					className="mb-17"
					outValue={userData.last_name}
					setOutValue={updateLastname}
					errorMessage={errors.last_name}
				/>

				<DatePicker
					onChange={updateBirthday}
					value={userData.birthday ? dayjs(userData.birthday) : ""}
					format={"DD.MM.YYYY"}
					showNow={false}
					showToday={false}
					clearIcon={false}
					suffixIcon={<CalendarIcon />}
					placeholder={"Date of Birth"}
					status={errors.birthday ? "error" : undefined}
				/>
				<div className="auth__block-text auth__block-text_align-start">
					<span className="auth__block-text_middle">Gender</span>
				</div>
				<div className="auth__block auth__block_align-start mb-20">
					<div className="auth__block-text">
						<Checkbox className="mr-12" checked={!userData.gender} shape="circle" onChange={() => updateGender("")} />
						<span className="auth__block-text_middle">Don’t choose</span>
					</div>
					<div className="auth__block-text">
						<Checkbox
							className="mr-12"
							checked={userData.gender === "M"}
							shape="circle"
							onChange={() => updateGender("M")}
						/>
						<span className="auth__block-text_middle">Male</span>
					</div>
					<div className="auth__block-text">
						<Checkbox
							className="mr-12"
							checked={userData.gender === "F"}
							shape="circle"
							onChange={() => updateGender("F")}
						/>
						<span className="auth__block-text_middle">Female</span>
					</div>
				</div>
				<DropdownCustom
					placeholder="Country"
					items={countryItems}
					onSelect={updateCountry}
					selectedKey={userData.country_id}
					selectedLabel={countryLabel()}
				/>
				<DropdownCustom
					placeholder="Time zone"
					items={timeZoneItems}
					onSelect={updateTz}
					selectedKey={userData.timezone_id}
					selectedLabel={timezoneLabel()}
				/>

				<Button text="Save" className={"mb-5"} onClick={onSave} isLoading={authFetch} />
			</div>
		</div>
	);
};
